import { Inject, Injectable } from '@angular/core';
import { LocalStorageService } from '@ndk/ng-storage';
import { IntlService } from '@progress/kendo-angular-intl';
import { NDDAbstractIntlService } from 'ndd-ng-intl';

import { AuthTokenService } from '@core/authentication/auth-token.service';
import { RoleTypes } from '@core/authentication/shared/role-types.enum';
import { LocalStorageKeys } from '@core/storage/local-storage.enum';

/** Classe apenas para override, usar `NDDIntlService` invés. */
@Injectable()
export class CustomIntlService extends NDDAbstractIntlService {
    public static LOCAL_STORAGE_CULTURE_KEY: string = 'NDD360IntlCulture';
    public static LOCAL_STORAGE_CURRENCY_CULTURE_KEY: string = LocalStorageKeys.CurrencyCulture;
    public static LOCAL_STORAGE_CURRENCY_SYMBOL_KEY: string = 'NDD360IntlCurrencySymbol';
    public static LOCAL_STORAGE_REGIONAL_SETTINGS_KEY: string = 'NDD360RegionalSettings';

    private isPartner: boolean;

    constructor(
        private authTokenService: AuthTokenService,
        private intlService: IntlService,
        private authenticationTokenService: AuthTokenService,
        @Inject(Window) private windowRef: Window,
        private localStorageService: LocalStorageService,
    ) {
        super(
            {
                keys: {
                    cultureKey: CustomIntlService.LOCAL_STORAGE_CULTURE_KEY,
                    currencyCultureKey: CustomIntlService.LOCAL_STORAGE_CURRENCY_CULTURE_KEY,
                },
            },
            intlService,
            windowRef,
        );
        // tslint:disable-next-line:no-bitwise
        this.isPartner = !!(this.authenticationTokenService.role & RoleTypes.Partners);
    }
    public get culture(): string {
        if (this.isPartner) {
            const settings: any = this.localStorageService
                .getValue(CustomIntlService.LOCAL_STORAGE_REGIONAL_SETTINGS_KEY);

            return settings
                ? settings.regionalSettings
                : 'pt-BR';
        }

        return this.localStorageService
            .getValue(CustomIntlService.LOCAL_STORAGE_CULTURE_KEY);
    }

    public set culture(name: string) {
        this.localStorageService
            .setValue(CustomIntlService.LOCAL_STORAGE_CULTURE_KEY, this.formatCulturePattern(name));
    }

    public currencySymbol(culture: string = this.currencyCulture || this.culture): string {
        const symbolCulture: string = this.localStorageService.getValue(CustomIntlService.LOCAL_STORAGE_CURRENCY_CULTURE_KEY);
        let symbol: string = this.localStorageService.getValue(CustomIntlService.LOCAL_STORAGE_CURRENCY_SYMBOL_KEY);

        if (!symbolCulture) {
            this.currencyCulture = this.authTokenService.token.cctr || 'pt-BR';
        }

        if (!symbol) {
            symbol = this.authTokenService.token.csym || 'R$';

            this.localStorageService
                .setValue(CustomIntlService.LOCAL_STORAGE_CURRENCY_SYMBOL_KEY, symbol);
        }

        return symbol;
    }

    public formatNumber(value: number, format: string, culture: string = this.culture): string {
        const symbolCustom: string = this.currencySymbol();
        const symbolByBaseCulture: string = super.currencySymbol(this.culture);
        const symbolByBaseCurrencyCulture: string = super.currencySymbol(this.currencyCulture);
        let formattedNumber: string = this.intlService.formatNumber(value, format, culture);

        // Caso o valor contenha símbolo de moeda, formata conforme símbolo da moeda da empresa, mantendo a formatação numérica definida por usuário.
        if (formattedNumber.indexOf(symbolByBaseCulture) > -1) {
            if (this.currencyCulture && this.currencyCulture !== this.culture) {
                const formattedNumberByCurrencyCulture: string = this.intlService.formatNumber(value, format, this.currencyCulture);
                const symbolIsInitial: boolean = formattedNumberByCurrencyCulture.indexOf(symbolByBaseCurrencyCulture) === 0;

                formattedNumber = formattedNumber.replace(symbolByBaseCulture, '').trim();
                if (symbolIsInitial) {
                    formattedNumber = `${ symbolCustom } ${ formattedNumber }`;
                } else {
                    formattedNumber = `${ formattedNumber } ${ symbolCustom }`;
                }
            }
        }

        return formattedNumber;
    }
}
